import React from "react";
import MenuIcon from "@mui/icons-material/Menu";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import ScreenshotMonitorIcon from "@mui/icons-material/ScreenshotMonitor";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RedeemIcon from "@mui/icons-material/Redeem";
import MapIcon from "@mui/icons-material/Map";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  dsokMenus,
  easyPayMenus,
  getHomeMenus,
  getMarketingSection,
  getPartnerMenu,
  kioskMenus,
  mediaMenus,
  productMenus,
} from "app/layouts/shared/sidebars/Sidebar/menus";
import adminMenus from "app/layouts/shared/sidebars/Sidebar/adminMenus";
import { getPosSection } from "app/layouts/shared/sidebars/Sidebar/posMenus";
import { isEasyAccessOperator } from "app/utils/constants/roleMenuConfig";

/**
 * Builds sidebar context: flags and menu building blocks.
 * Preserves exact logic from Sidebar index. No behavior change.
 * @param {Object} params
 * @param {Object} params.customer - logged-in customer (may be undefined during bootstrap)
 * @param {number} params.type - user type
 * @param {string|number} params.userId
 * @param {Object} params.location - react-router location
 * @param {URLSearchParams|Object} params.searchParams
 * @param {Object} params.partnerUser - from usePartnerAuth
 * @returns {Object} context with isPartner, isServicePartnerCustomer, menus, etc.
 */
export function buildSidebarContext({
  customer = null,
  type,
  userId,
  location = {},
  searchParams = {},
  partnerUser,
}) {
  const customerSafe = customer || {};
  const dsokSettings = customerSafe.dsokSettings;
  const customerUISettings = {
    ...(dsokSettings?.customer?.customerUISettings || {}),
  };
  const accessSystemEnabled = dsokSettings?.settings?.accessSystem?.enabled;
  if (typeof accessSystemEnabled === "boolean") {
    customerUISettings.enableAccessSystem = accessSystemEnabled;
  }
  customerUISettings.enableDispensingOrders =
    customerSafe?.dsokSettings?.customer?.customerUISettings
      ?.enableDispensingOrders ?? false;
  customerUISettings.enableCartRules = Boolean(
    customerSafe?.dsokSettings?.settings?.enableCartRules,
  );

  const pathname = location?.pathname || "";
  const isPartner = pathname.startsWith("/manage");
  const token =
    typeof searchParams?.get === "function"
      ? searchParams.get("token")
      : (searchParams?.token ?? null);
  const lang =
    typeof searchParams?.get === "function"
      ? searchParams.get("hl")
      : (searchParams?.hl ?? "sk-SK");

  const isServicePartnerCustomer =
    customerSafe?.customerType === 7 || customerSafe?.customer_type === 7;
  const { kiosk, presentations, screens, templates, products, dsok, easyPay } =
    customerSafe;
  const superadmin =
    customerSafe?.customerType === 1 || customerSafe?.customerType === 2;
  const datavisionCustomer =
    customerSafe?.customerType === 3 && !customerSafe?.dsok;
  const dsokOperator = type === 4;
  const easyPayOperator = type === 5;
  const easyAccessOperator = isEasyAccessOperator(type);

  const adminMenuBase = superadmin && adminMenus;
  const adminMenu = adminMenuBase;
  const homeMenu = getHomeMenus({
    presentations,
    screens,
    templates,
    userId,
  });
  if ((dsok && !easyPay) || superadmin) {
    homeMenu.children.push({
      uri: "/dsok/faq",
      label: "sidebar.menuItem.faq",
      type: "nav-item",
      icon: <HelpOutlineIcon sx={{ fontSize: 20 }} />,
    });
  }
  const datavisionCustomerMenu = getHomeMenus({
    presentations,
    screens,
    templates,
    userId,
  });
  const kioskMenu = kiosk && kioskMenus;
  const dsokType = customerSafe?.dsokType;
  const productMenu = products && productMenus;
  const mediaMenu = !dsokOperator && mediaMenus;
  const partnerMenu = getPartnerMenu({ partnerUser, token, lang });

  const dsokMenu =
    dsok && !easyPay && dsokMenus(customerUISettings, dsokType, dsokSettings);

  const marketingSection =
    dsok &&
    !easyPay &&
    !dsokOperator &&
    getMarketingSection(customerUISettings, dsokType);

  const posSection = dsok && !easyPay && getPosSection(customerSafe);

  const accessSystemSection = dsok &&
    !easyPay &&
    customerUISettings.enableAccessSystem === true && {
      label: "sidebar.menuItem.accessSystem",
      type: "section",
      icon: <ReceiptLongIcon sx={{ fontSize: 20 }} />,
      children: [
        {
          uri: "/customer/dsok/access-system/tickets-management",
          label: "sidebar.menuItem.accessSystemTickets",
          type: "nav-item",
          icon: <ReceiptLongIcon sx={{ fontSize: 20 }} />,
        },
        {
          uri: "/customer/dsok/access-system/vouchers-management",
          label: "sidebar.menuItem.accessSystemVouchers",
          type: "nav-item",
          icon: <RedeemIcon sx={{ fontSize: 20 }} />,
        },
        {
          uri: "/customer/dsok/access-system/zones-management",
          label: "sidebar.menuItem.accessSystemZones",
          type: "nav-item",
          icon: <MapIcon sx={{ fontSize: 20 }} />,
        },
        {
          uri: "/customer/dsok/access-system/settings",
          label: "sidebar.menuItem.accessSystemSettings",
          type: "nav-item",
          icon: <ReceiptLongIcon sx={{ fontSize: 20 }} />,
        },
      ],
    };

  /** Menu for easyAccessOperator (userType 6): Access System → tickets + vouchers */
  const easyAccessOperatorMenu = easyAccessOperator && {
    label: "sidebar.menuItem.accessSystem",
    type: "section",
    icon: <ReceiptLongIcon sx={{ fontSize: 20 }} />,
    children: [
      {
        uri: "/customer/dsok/access-system/tickets-management",
        label: "sidebar.menuItem.accessSystemTickets",
        type: "nav-item",
        icon: <ReceiptLongIcon sx={{ fontSize: 20 }} />,
      },
      {
        uri: "/customer/dsok/access-system/vouchers-management",
        label: "sidebar.menuItem.accessSystemVouchers",
        type: "nav-item",
        icon: <RedeemIcon sx={{ fontSize: 20 }} />,
      },
      {
        uri: "/customer/dsok/access-system/zones-management",
        label: "sidebar.menuItem.accessSystemZones",
        type: "nav-item",
        icon: <MapIcon sx={{ fontSize: 20 }} />,
      },
    ],
  };

  const easyPayMenu = easyPay && easyPayMenus;
  const dsokMenuResult =
    dsok && !easyPay && dsokMenus(customerUISettings, dsokType, dsokSettings);
  const dsokOperatorMenu =
    dsok && dsokMenuResult
      ? {
          ...dsokMenuResult,
          children: (dsokMenuResult.children || []).filter(
            (menu) => menu.operatorAccess,
          ),
        }
      : {};

  const dailyMenuSection =
    dsok &&
    !easyPay &&
    (customerUISettings.enableDailyMenu === true ||
      customerUISettings.enableMenuManager === true) &&
    (() => {
      const children = [];
      if (customerUISettings.enableDailyMenu === true) {
        children.push({
          uri: "/customer/dsok/daily-menu",
          label: "sidebar.menuItem.dsokDailyMenu",
          type: "nav-item",
          icon: <RestaurantMenuIcon sx={{ fontSize: 20 }} />,
        });
      }
      if (customerUISettings.enableMenuManager === true) {
        children.push({
          uri: "/customer/dsok/boards",
          label: "sidebar.menuItem.menuManager",
          type: "nav-item",
          icon: <ScreenshotMonitorIcon sx={{ fontSize: 20 }} />,
        });
      }
      return children.length
        ? {
            label: "sidebar.menu.dailyMenuList",
            type: "section",
            icon: <RestaurantMenuIcon />,
            children,
          }
        : null;
    })();

  const others =
    productMenu || kioskMenu || dsokMenu || easyPayMenu || dailyMenuSection
      ? {
          label: "sidebar.menu.others",
          type: "section",
          icon: <MenuIcon />,
          children: [
            productMenu,
            kioskMenu,
            easyPayMenu,
            dsokOperator ? dsokOperatorMenu : dsokMenu,
            marketingSection,
            posSection,
            accessSystemSection,
            dailyMenuSection,
            mediaMenu,
          ].filter(Boolean),
        }
      : null;

  return {
    customer: customerSafe,
    type,
    isPartner,
    isServicePartnerCustomer,
    superadmin,
    datavisionCustomer,
    dsokOperator,
    easyPayOperator,
    easyAccessOperator,
    easyAccessOperatorMenu,
    homeMenu,
    adminMenu,
    datavisionCustomerMenu,
    kioskMenu,
    productMenu,
    mediaMenu,
    partnerMenu,
    others,
    kiosk,
  };
}
